import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { MessageSquare, Bug, Lightbulb, Send, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE } from '../apiConfig';

const API = API_BASE;

const TYPES = [
  { value: 'bug', label: 'Bug Report', icon: Bug, color: 'bg-red-50 border-red-200 text-red-700', activeColor: 'bg-red-100 border-red-300' },
  { value: 'feature', label: 'Feature Request', icon: Lightbulb, color: 'bg-amber-50 border-amber-200 text-amber-700', activeColor: 'bg-amber-100 border-amber-300' },
  { value: 'general', label: 'General Feedback', icon: MessageSquare, color: 'bg-indigo-50 border-indigo-200 text-indigo-700', activeColor: 'bg-indigo-100 border-indigo-300' },
];

export const FeedbackModal = ({ open, onClose }) => {
  const [type, setType] = useState('general');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async () => {
    if (!message.trim()) { toast.error('Please write your feedback'); return; }
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ feedback_type: type, message: message.trim(), page: window.location.pathname }),
      });
      if (r.ok) {
        setSubmitted(true);
        setTimeout(() => { onClose(); setSubmitted(false); setMessage(''); setType('general'); }, 2000);
      } else {
        toast.error('Failed to submit feedback');
      }
    } catch (e) {
      toast.error('Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white rounded-2xl max-w-md" data-testid="feedback-modal">
        {submitted ? (
          <div className="py-8 text-center">
            <div className="w-14 h-14 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-7 h-7 text-emerald-600" />
            </div>
            <h3 className="text-lg font-heading font-semibold text-slate-900">Thank you!</h3>
            <p className="text-sm text-slate-500 mt-1">Your feedback has been submitted.</p>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading text-lg text-slate-900 flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-indigo-500" /> Send Feedback
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              {/* Type selector */}
              <div className="grid grid-cols-3 gap-2">
                {TYPES.map(t => {
                  const Icon = t.icon;
                  const active = type === t.value;
                  return (
                    <button
                      key={t.value}
                      onClick={() => setType(t.value)}
                      className={`p-3 rounded-xl border text-center transition-all ${active ? t.activeColor : t.color} ${active ? 'ring-2 ring-offset-1 ring-slate-300' : ''}`}
                      data-testid={`feedback-type-${t.value}`}
                    >
                      <Icon className="w-5 h-5 mx-auto mb-1" />
                      <span className="text-xs font-medium">{t.label}</span>
                    </button>
                  );
                })}
              </div>

              {/* Message */}
              <Textarea
                placeholder={type === 'bug' ? "Describe the bug — what happened and what you expected..." : type === 'feature' ? "What feature would help you teach better?" : "Share your thoughts, suggestions, or experience..."}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="border-slate-200 rounded-xl min-h-[120px] resize-none text-sm"
                data-testid="feedback-message"
              />

              <Button
                onClick={handleSubmit}
                disabled={submitting || !message.trim()}
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl"
                data-testid="submit-feedback-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                {submitting ? 'Sending...' : 'Submit Feedback'}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
